import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '@siaedts/shared/lib/supabase';
import heroFarmImage from '../assets/hero-farm.jpg';

interface HomeProduct {
  id: string;
  name: string;
  slug: string;
  description: string;
  image?: string;
  category: string;
  price?: number | null;
  currency?: string;
}

interface HomeNews {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  image?: string;
  publishedAt: string;
}

interface HomePartner {
  id: string;
  name: string;
  logo_url?: string | null;
  website_url?: string | null;
  sort_order?: number;
}

function formatPrice(price: number | null | undefined, currency = 'USD') {
  if (price === null || price === undefined) return 'Request a quote';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
}

const features = [
  {
    key: 'quality',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.429 15.429a12.062 12.062 0 01-1.587 1.221 3.21 3.21 0 01-4.828 0 3.21 3.21 0 010-4.542V9a2 2 0 114 0v3.586c0 .618.224 1.212.591 1.629l-1.27 1.27a3.21 3.21 0 010 4.542c1.205 1.204 3.155 1.204 4.36 0l1.415-1.414a3.21 3.21 0 010-4.542zM12 6V4a2 2 0 114 0v2m0 0a2 2 0 104 0m-4 0a2 2 0 11-4 0" />
      </svg>
    ),
  },
  {
    key: 'support',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    key: 'delivery',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    key: 'warranty',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const stats = [
  { value: '500+', key: 'products' },
  { value: '50+', key: 'brands' },
  { value: '10K+', key: 'customers' },
  { value: '15+', key: 'years' },
];

export function Home() {
  const { t } = useTranslation();
  const [featuredProducts, setFeaturedProducts] = useState<HomeProduct[]>([]);
  const [latestNews, setLatestNews] = useState<HomeNews[]>([]);
  const [trustedPartners, setTrustedPartners] = useState<HomePartner[]>([]);

  useEffect(() => {
    let active = true;
    void Promise.all([
      db.products.getAll({ status: 'available', featured: true, limit: 3 }),
      db.news.getAll({ status: 'published', limit: 3 }),
      db.partners.getAll({ is_active: true }),
    ]).then(([products, news, partners]) => {
      if (!active) return;
      setFeaturedProducts((products as unknown as Array<{ id: string; name: string; slug: string; short_description?: string | null; description?: string | null; image_url?: string | null; category?: { name: string } | null; price?: number | null; currency?: string }>).map((product) => ({
        id: product.id,
        name: product.name,
        slug: product.slug,
        description: product.short_description ?? product.description ?? '',
        image: product.image_url ?? undefined,
        category: product.category?.name ?? 'Accessories',
        price: product.price,
        currency: product.currency,
      })));
      setLatestNews((news as unknown as Array<{ id: string; title: string; slug: string; excerpt?: string | null; featured_image_url?: string | null; published_at?: string | null }>).map((article) => ({
        id: article.id,
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt ?? '',
        image: article.featured_image_url ?? undefined,
        publishedAt: article.published_at ?? new Date().toISOString(),
      })));
      setTrustedPartners((partners as unknown as HomePartner[]).sort((left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0)));
    }).catch(() => {
      if (active) {
        setFeaturedProducts([]);
        setLatestNews([]);
        setTrustedPartners([]);
      }
    });
    return () => { active = false; };
  }, []);
  return (
    <>
      {/* Hero Section */}
      <section className="home-hero relative overflow-hidden">
        <div className="home-hero-grid" aria-hidden="true" />
        <div className="container-main relative py-16 lg:py-24">
          <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-12 items-center">
          <div className="max-w-2xl">
            <span className="home-kicker animate-fade-in">
              {t('home.heroBadge')}
            </span>
            <h1 className="home-hero-title animate-slide-up">
              {t('home.heroTitle1')}{' '}
              <span>{t('home.heroTitle2')}</span>
            </h1>
            <p className="home-hero-copy animate-slide-up" style={{ animationDelay: '100ms' }}>
              {t('home.heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 animate-slide-up" style={{ animationDelay: '200ms' }}>
              <Link to="/products" className="btn-primary text-base px-7 py-4">
                {t('home.exploreProducts')}
              </Link>
              <Link to="/services" className="home-hero-link">
                {t('home.ourServices')}
              </Link>
              <Link to="/contact" className="home-hero-link">
                {t('home.getQuote')}
              </Link>
            </div>
          </div>
          <div className="home-hero-visual" aria-label="Agricultural equipment and service showcase">
            <img className="home-hero-image" src={heroFarmImage} alt="Greenhouse crops growing for a productive harvest" />
            <div className="home-hero-image-shade" aria-hidden="true" />
            <div className="hero-stat-card hero-stat-top"><span>FIELD NOTE 01</span><strong>Built for the next harvest.</strong></div>
            <div className="hero-stat-card hero-stat-bottom"><span>SI AEDTS / GUINEA</span><strong>Equipment. Expertise. Momentum.</strong></div>
          </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="home-stats text-white py-12">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat) => (
              <div key={stat.key} className="animate-fade-in">
                <div className="text-4xl sm:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-green-100">{t(`home.stats.${stat.key}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section home-feature-section bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="section-title">{t('home.whyChooseTitle')}</h2>
            <p className="section-subtitle">
              {t('home.whyChooseSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.key} 
                className="card p-6 text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4 text-green-700">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t(`home.features.${feature.key}.title`)}</h3>
                <p className="text-gray-600">{t(`home.features.${feature.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="section home-products-section bg-gray-50">
        <div className="container-main">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12">
            <div>
              <h2 className="section-title">{t('home.featuredProductsTitle')}</h2>
              <p className="section-subtitle">{t('home.featuredProductsSubtitle')}</p>
            </div>
            <Link to="/products" className="btn-outline mt-4 sm:mt-0">
              {t('common.viewAllProducts')}
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((product) => <article key={product.id} className="card group"><Link to={`/products/${product.slug}`} className="block"><div className="aspect-video bg-gray-100 relative overflow-hidden">{product.image ? <img src={product.image} alt={product.name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-400">No image</div>}<div className="absolute top-3 left-3"><span className="badge badge-blue">{product.category}</span></div></div><div className="p-6"><h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3><p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p><div className="flex items-center justify-between"><span className="text-xl font-bold text-green-700">{formatPrice(product.price, product.currency)}</span><span className="btn-primary text-sm py-2 px-4">{t('common.viewDetails')}</span></div></div></Link></article>)}
            {featuredProducts.length === 0 && <p className="col-span-full text-gray-500">No featured products published yet.</p>}
          </div>
        </div>
      </section>

      <section className="section home-news-section bg-white">
        <div className="container-main"><div className="flex items-end justify-between mb-12"><div><h2 className="section-title">Latest News</h2><p className="section-subtitle">Updates and announcements from SIAEDTS-SARL.</p></div><Link to="/news" className="btn-outline">View all news</Link></div><div className="grid grid-cols-1 md:grid-cols-3 gap-8">{latestNews.map((article) => <article key={article.id} className="card"><Link to={`/news/${article.slug}`}><div className="aspect-video bg-gray-100">{article.image && <img src={article.image} alt={article.title} className="h-full w-full object-cover" />}</div><div className="p-6"><time className="text-sm text-gray-500">{new Date(article.publishedAt).toLocaleDateString()}</time><h3 className="mt-2 text-lg font-semibold">{article.title}</h3><p className="mt-2 text-sm text-gray-600 line-clamp-2">{article.excerpt}</p></div></Link></article>)}{latestNews.length === 0 && <p className="col-span-full text-gray-500">No published news yet.</p>}</div></div>
      </section>

      {/* CTA Section */}
      <section className="section bg-green-700">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-green-100 text-lg mb-8">
              {t('home.ctaDescription')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" className="btn bg-white text-green-700 hover:bg-green-50 text-lg px-8 py-4">
                {t('home.requestQuote')}
              </Link>
              <Link to="/products" className="btn border-2 border-white text-white hover:bg-green-600 text-lg px-8 py-4">
                {t('home.browseProducts')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners/Brands Section */}
      <section className="section bg-white border-t border-gray-200">
        <div className="container-main">
          <p className="text-center text-gray-500 text-sm mb-8 uppercase tracking-wider">{t('home.trustedBrands')}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
            {trustedPartners.map((partner) => {
              const brand = <div className="text-center">
                {partner.logo_url ? <img src={partner.logo_url} alt={partner.name} className="mx-auto h-12 max-w-[150px] object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all" /> : <div className="text-2xl font-bold text-gray-300 hover:text-green-700 transition-colors">{partner.name}</div>}
              </div>;
              return partner.website_url ? <a key={partner.id} href={partner.website_url} target="_blank" rel="noreferrer">{brand}</a> : <div key={partner.id}>{brand}</div>;
            })}
            {trustedPartners.length === 0 && <p className="col-span-full text-center text-gray-400">No trusted partners published yet.</p>}
          </div>
        </div>
      </section>
    </>
  );
}